import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircleCheckBig, Loader2 } from 'lucide-react';
import { DetailPanel } from '@/components/common/DetailPanel';
import { FormTextareaField } from '@/components/common/FormDialog';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { ADMIN_MESSAGE_MAX } from '../constants/feedback.constants';
import { useCompleteFeedback } from '../hooks/useCompleteFeedback';
import { completeFeedbackSchema, type CompleteFeedbackPayload } from '../types/feedback.types';

const EMPTY_COMMENT: CompleteFeedbackPayload = { adminMessage: '' };

interface FeedbackCompleteFormProps {
  feedbackId: number;
}

export function FeedbackCompleteForm({ feedbackId }: FeedbackCompleteFormProps) {
  const form = useForm<CompleteFeedbackPayload>({
    resolver: zodResolver(completeFeedbackSchema),
    defaultValues: EMPTY_COMMENT,
  });
  const completeMutation = useCompleteFeedback();

  function handleComplete(values: CompleteFeedbackPayload) {
    const comment = values.adminMessage?.trim();
    completeMutation.mutate({
      id: feedbackId,
      payload: comment ? { adminMessage: comment } : {},
    });
  }

  return (
    <DetailPanel title="Complete Feedback" icon={CircleCheckBig}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleComplete)} className="flex flex-col gap-3">
          <FormTextareaField
            control={form.control}
            name="adminMessage"
            label="Comment"
            placeholder="Add a comment for this feedback (optional)"
            maxLength={ADMIN_MESSAGE_MAX}
          />
          <Button type="submit" disabled={completeMutation.isPending} className="self-end">
            {completeMutation.isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                Saving...
              </>
            ) : (
              'Complete'
            )}
          </Button>
        </form>
      </Form>
    </DetailPanel>
  );
}
